import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Edit3,
  Plus,
  Trash2,
  DollarSign,
  Save,
  ArrowLeft,
  Percent,
} from 'lucide-react';
import { api } from '../api/client';
import { instantTenantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { fmtCurrency, fmtUnitRate, rateInputValue } from '../lib/display-labels';
import DatePicker from '../components/DatePicker';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LineItem {
  id: string;
  type: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  total: number;
  isDiscount?: boolean;
}

interface Payment {
  id: string;
  amount: number;
  method: string;
  date: string;
  reference?: string;
}

interface LinkedTicket {
  id: string;
  ticketNumber: string;
  date: string;
  total: number;
}

/**
 * WHAT `GET /invoices/:id` ACTUALLY SENDS — checked against a live response on
 * 2026-09-22, not against this interface, because this interface was the bug.
 *
 * It declared `invoiceDate`, `paymentTerms`, `notes`, `contactName` and
 * `contactEmail`. The endpoint hand-built its response object and emitted NONE
 * of those five names: the address was `customerEmail`, and the other four were
 * not on the payload at all. TypeScript agreed with every read because the
 * interface declared the wrong fields too — the repo's #1 recurring bug, and
 * the reason `api-manifest.d.ts` exists.
 *
 * THE FILED FIX LANDED (api#798). `GET /invoices/:id` now emits `invoiceDate`,
 * `paymentTerms`, `notes`, `customerContactName` and `customerContactEmail` —
 * every column `PUT /invoices/:id` writes, under the key it writes — pinned on
 * that side by `invoice-read-emits-write-keys.test.ts`.
 *
 * THIS PR MOVES THE ADDRESS ONLY, AND THE OTHER FOUR STAY UNREAD ON PURPOSE.
 * Reading them is not a rename: it would make four inputs that currently load
 * blank start carrying values, which changes what a save posts and what
 * `touched` means, and that deserves its own verification rather than a ride
 * on a key change. Until then those four still load as `''` and are still
 * posted only when touched — the docblock on `touched` below is unchanged and
 * still correct.
 */
interface Invoice {
  id: string;
  invoiceNumber: string;
  status: string;
  dueDate: string;
  /** The recipient. ONE spelling on both sides of the wire now: the read
   *  emitted only `customerEmail` until api#798, the write has only ever taken
   *  `customerContactEmail`, and the read's deprecated alias is removed from
   *  the API in the PR after this one. Declared without a fallback so a stale
   *  binding fails visibly rather than quietly surviving the removal.
   *  InvoiceDetailScreen reads the same key. */
  customerContactEmail?: string;
  subtotal: number;
  discountTotal: number;
  tax1Amount: number;
  tax2Amount: number;
  total: number;
  balanceDue: number;
  lineItems: LineItem[];
  payments: Payment[];
  tickets: LinkedTicket[];
  /* EMITTED BY THE READ SINCE api#798, and still deliberately not declared:
     invoiceDate, paymentTerms, notes, customerContactName. Not because the
     payload lacks them any more — it does not — but because nothing in this
     file reads them yet, and a declared-but-unread field is how the next
     person concludes the pre-fill already works. Declare them in the PR that
     wires them up. */
}

// ─── Helpers ──────────────────────────────────────────────────────────────────


// ─── Component ────────────────────────────────────────────────────────────────

export default function InvoiceEditScreen() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const queryClient = useQueryClient();

  // ─── Toast ────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    setToast({ message, type });
  }

  // ─── Invoice query ────────────────────────────────────────────────────
  const {
    data: invoice,
    isLoading,
    isError,
  } = useQuery<Invoice>({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const res = await api.invoices.get(invoiceId!);
      return res.data.data;
    },
    enabled: !!invoiceId,
  });

  // ─── Invoice info form state ──────────────────────────────────────────

  /**
   * THE BODY KEYS `PUT /invoices/:id` ALLOWLISTS, for the five fields this form
   * owns. Read off the handler, not guessed: anything else is dropped and
   * answered 200 with the full row, which tells the caller the value landed.
   *
   * `contactName` / `contactEmail` were sent here and are not among them. They
   * were the whole of api#792's second finding.
   */
  const INFO_BODY_KEYS = {
    invoiceDate: 'invoiceDate',
    dueDate: 'dueDate',
    paymentTerms: 'paymentTerms',
    notes: 'notes',
    contactName: 'customerContactName',
    contactEmail: 'customerContactEmail',
  } as const;

  const [infoForm, setInfoForm] = useState({
    invoiceDate: '',
    dueDate: '',
    paymentTerms: '',
    notes: '',
    contactName: '',
    contactEmail: '',
  });

  /**
   * WHICH FIELDS THE USER HAS ACTUALLY TOUCHED — and the reason the save is not
   * simply `mutate(infoForm)`.
   *
   * Four of these six cannot be pre-filled: the read does not emit
   * `invoiceDate`, `paymentTerms`, `notes` or the contact name (see the Invoice
   * interface above). So they load as `''`, and `''` is a LEGAL VALUE of the
   * right type meaning "the office cleared this field".
   *
   * The API applies each key with `if (body.X !== undefined)`, so posting the
   * whole form said "clear them" about four fields nobody had looked at:
   * `invoice_date` to NULL, `payment_terms` and `notes` to the empty string, on
   * any save at all — the button only needs ONE field dirty to unlock. Two
   * states, one spelling, and the destructive one is the default.
   *
   * Omitting an untouched key is how you say "leave this column alone", so the
   * set of touched fields is what decides the body. Measured on production
   * 2026-09-22 before changing anything: 0 of 94 invoices carry a NULL
   * invoice_date and 0 have blank payment terms, so this has not fired yet.
   * Closed before it does, not after.
   */
  const [touched, setTouched] = useState<Set<keyof typeof infoForm>>(new Set());
  const infoFormDirty = touched.size > 0;

  useEffect(() => {
    if (invoice) {
      setInfoForm({
        // The two the read can supply.
        // An INSTANT (api#838), seeded as the TENANT's day — its UTC prefix is
        // tomorrow for an invoice written after 18:00 MDT. An untouched date is
        // never posted (see `touched`), so the stored instant is left exactly
        // as it was; the day shown here is only what a change would start from.
        dueDate: instantTenantDay(invoice.dueDate, timeZone) ?? '',
        contactEmail: invoice.customerContactEmail || '',
        // The four it cannot. Blank is not a claim that the column is blank —
        // it is this screen having nothing to show, which is why an untouched
        // one is never posted.
        invoiceDate: '',
        paymentTerms: '',
        notes: '',
        contactName: '',
      });
      setTouched(new Set());
    }
  }, [invoice, timeZone]);

  const updateInfoMutation = useMutation({
    mutationFn: (data: Record<string, string>) => api.invoices.update(invoiceId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      showToast('Invoice info saved');
      setTouched(new Set());
    },
    onError: () => showToast('Failed to save invoice info', 'error'),
  });

  function handleInfoChange(field: keyof typeof infoForm, value: string) {
    setInfoForm((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => new Set(prev).add(field));
  }

  function handleSaveInfo() {
    const body: Record<string, string> = {};
    // Array.from rather than a for-of over the Set: this project's tsconfig
    // targets below es2015, where iterating a Set needs --downlevelIteration.
    Array.from(touched).forEach((field) => {
      body[INFO_BODY_KEYS[field]] = infoForm[field];
    });
    updateInfoMutation.mutate(body);
  }

  // ─── Line item editing ────────────────────────────────────────────────
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLineForm, setEditLineForm] = useState({ description: '', quantity: '', rate: '' });

  function startEditLine(item: LineItem) {
    setEditingLineId(item.id);
    setEditLineForm({
      description: item.description || '',
      quantity: String(item.quantity ?? ''),
      rate: rateInputValue(item.rate),
    });
  }

  function cancelEditLine() {
    setEditingLineId(null);
  }

  const updateLineItemMutation = useMutation({
    mutationFn: ({ lineItemId, data }: { lineItemId: string; data: any }) =>
      api.invoices.updateLineItem(invoiceId!, lineItemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      setEditingLineId(null);
      showToast('Line item updated');
    },
    onError: () => showToast('Failed to update line item', 'error'),
  });

  function handleSaveLineItem(lineItemId: string) {
    updateLineItemMutation.mutate({
      lineItemId,
      data: {
        description: editLineForm.description,
        quantity: parseFloat(editLineForm.quantity) || 0,
        rate: parseFloat(editLineForm.rate) || 0,
      },
    });
  }

  const deleteLineItemMutation = useMutation({
    mutationFn: (lineItemId: string) => api.invoices.deleteLineItem(invoiceId!, lineItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      showToast('Line item deleted');
    },
    onError: () => showToast('Failed to delete line item', 'error'),
  });

  function handleDeleteLineItem(lineItemId: string) {
    if (!window.confirm('Delete this line item?')) return;
    deleteLineItemMutation.mutate(lineItemId);
  }

  // ─── Add Custom Line ──────────────────────────────────────────────────
  const [showAddLine, setShowAddLine] = useState(false);
  const [addLineForm, setAddLineForm] = useState({
    type: 'service',
    description: '',
    unit: 'each',
    rate: '',
    quantity: '1',
  });

  const addLineItemMutation = useMutation({
    mutationFn: (data: any) => api.invoices.addLineItem(invoiceId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      setShowAddLine(false);
      setAddLineForm({ type: 'service', description: '', unit: 'each', rate: '', quantity: '1' });
      showToast('Line item added');
    },
    onError: () => showToast('Failed to add line item', 'error'),
  });

  function handleAddLineItem() {
    if (!addLineForm.description || !addLineForm.rate) return;
    addLineItemMutation.mutate({
      type: addLineForm.type,
      description: addLineForm.description,
      unit: addLineForm.unit,
      rate: parseFloat(addLineForm.rate) || 0,
      quantity: parseFloat(addLineForm.quantity) || 1,
    });
  }

  // ─── Add Discount ─────────────────────────────────────────────────────
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    discountType: 'flat' as 'flat' | 'percent',
    amount: '',
    percent: '',
    description: 'Discount',
  });

  const addDiscountMutation = useMutation({
    mutationFn: (data: any) => api.invoices.addDiscount(invoiceId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      setShowAddDiscount(false);
      setDiscountForm({ discountType: 'flat', amount: '', percent: '', description: 'Discount' });
      showToast('Discount added');
    },
    onError: () => showToast('Failed to add discount', 'error'),
  });

  function handleAddDiscount() {
    const payload: any = {
      description: discountForm.description,
      discountType: discountForm.discountType,
    };
    if (discountForm.discountType === 'flat') {
      payload.amount = parseFloat(discountForm.amount) || 0;
    } else {
      payload.percent = parseFloat(discountForm.percent) || 0;
    }
    addDiscountMutation.mutate(payload);
  }

  // ─── Add Ticket ───────────────────────────────────────────────────────
  const [showAddTicket, setShowAddTicket] = useState(false);
  const [ticketSearch, setTicketSearch] = useState('');

  const { data: allTickets } = useQuery({
    queryKey: ['tickets-for-invoice'],
    queryFn: async () => {
      const res = await api.tickets.list();
      return (res.data.data ?? []) as any[];
    },
    enabled: showAddTicket,
  });

  const filteredTickets = (allTickets || []).filter((t: any) => {
    const linked = (invoice?.tickets || []).map((lt) => lt.id);
    if (linked.includes(t.id)) return false;
    if (!ticketSearch) return true;
    const q = ticketSearch.toLowerCase();
    return (
      (t.ticketNumber || '').toLowerCase().includes(q) ||
      (t.customer || '').toLowerCase().includes(q)
    );
  });

  const addTicketMutation = useMutation({
    mutationFn: (ticketId: string) => api.invoices.addTicket(invoiceId!, ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      setShowAddTicket(false);
      setTicketSearch('');
      showToast('Ticket added to invoice');
    },
    onError: () => showToast('Failed to add ticket', 'error'),
  });

  // ─── Remove Ticket ────────────────────────────────────────────────────
  const [showRemoveTicket, setShowRemoveTicket] = useState(false);

  const removeTicketMutation = useMutation({
    mutationFn: (ticketId: string) => api.invoices.removeTicket(invoiceId!, ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      showToast('Ticket removed from invoice');
    },
    onError: () => showToast('Failed to remove ticket', 'error'),
  });

  function handleRemoveTicket(ticketId: string) {
    if (!window.confirm('Remove this ticket from the invoice?')) return;
    removeTicketMutation.mutate(ticketId);
  }

  // ─── Preview PDF ──────────────────────────────────────────────────────
  function handlePreviewPdf() {
    api.invoices
      .getPdf(invoiceId!)
      .then((res) => {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch(() => showToast('Failed to load PDF', 'error'));
  }

  // ─── Loading / Error ──────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark flex items-center justify-center">
        <div className="text-fieldiq-muted text-lg">Loading invoice...</div>
      </div>
    );
  }

  if (isError || !invoice) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Failed to load invoice</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-32">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">Edit Invoice</h1>
            <p className="text-fieldiq-muted text-sm">{invoice.invoiceNumber}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveInfo}
              disabled={!infoFormDirty || updateInfoMutation.isPending}
              className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2 flex items-center gap-2 disabled:opacity-40"
            >
              <Save size={16} />
              {updateInfoMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-4 space-y-4">
        {/* ── Invoice Info Section ───────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-4">
          <h2 className="text-white font-semibold text-base flex items-center gap-2">
            <Edit3 size={16} className="text-fieldiq-accent" />
            Invoice Details
          </h2>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Invoice Date</label>
              <DatePicker value={infoForm.invoiceDate} onChange={(v) => handleInfoChange('invoiceDate', v)} />
            </div>
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Due Date</label>
              <DatePicker value={infoForm.dueDate} onChange={(v) => handleInfoChange('dueDate', v)} />
            </div>
          </div>

          <div>
            <label className="text-fieldiq-muted text-xs mb-1 block">Payment Terms</label>
            <input
              type="text"
              value={infoForm.paymentTerms}
              onChange={(e) => handleInfoChange('paymentTerms', e.target.value)}
              placeholder="e.g. Net 30"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>

          <div>
            <label className="text-fieldiq-muted text-xs mb-1 block">Notes</label>
            <textarea
              value={infoForm.notes}
              onChange={(e) => handleInfoChange('notes', e.target.value)}
              placeholder="Internal notes..."
              rows={3}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Contact Name</label>
              <input
                type="text"
                value={infoForm.contactName}
                onChange={(e) => handleInfoChange('contactName', e.target.value)}
                placeholder="Contact name"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Contact Email</label>
              <input
                type="text"
                value={infoForm.contactEmail}
                onChange={(e) => handleInfoChange('contactEmail', e.target.value)}
                placeholder="email@example.com"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>

          {/* A fact about the form the reader would otherwise get wrong, and
              the reason four of these boxes can look empty on an invoice that
              is not: a field nobody typed in is not sent, so it is not
              changed. Not a description of the app's internals — it is the
              permission to leave a blank box alone. */}
          <p className="text-fieldiq-muted text-xs">
            Fields you do not edit are left as they are.
          </p>
        </div>

        {/* ── Line Items ─────────────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
          <h2 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
            <DollarSign size={16} className="text-fieldiq-accent" />
            Line Items
          </h2>

          {(invoice.lineItems || []).length === 0 && (
            <p className="text-fieldiq-muted text-sm text-center py-6">No line items yet.</p>
          )}

          <div className="space-y-2">
            {(invoice.lineItems || []).map((item) => {
              const isEditing = editingLineId === item.id;
              const isDiscount = item.isDiscount || item.type === 'discount';

              if (isEditing) {
                return (
                  <div
                    key={item.id}
                    className="bg-fieldiq-dark rounded-lg p-3 border border-fieldiq-accent/40 space-y-2"
                  >
                    <input
                      value={editLineForm.description}
                      onChange={(e) =>
                        setEditLineForm((p) => ({ ...p, description: e.target.value }))
                      }
                      placeholder="Description"
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-fieldiq-muted text-xs mb-1 block">Qty</label>
                        <input
                          type="number"
                          value={editLineForm.quantity}
                          onChange={(e) =>
                            setEditLineForm((p) => ({ ...p, quantity: e.target.value }))
                          }
                          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-fieldiq-muted text-xs mb-1 block">Rate</label>
                        <input
                          type="number"
                          value={editLineForm.rate}
                          onChange={(e) =>
                            setEditLineForm((p) => ({ ...p, rate: e.target.value }))
                          }
                          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={cancelEditLine}
                        className="text-fieldiq-muted text-sm px-3 py-1 rounded-lg hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveLineItem(item.id)}
                        disabled={updateLineItemMutation.isPending}
                        className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2 text-sm flex items-center gap-1 disabled:opacity-40"
                      >
                        <Save size={14} />
                        {updateLineItemMutation.isPending ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.id}
                  className={`bg-fieldiq-dark rounded-lg p-3 flex items-start justify-between gap-2 ${
                    isDiscount ? 'border border-red-500/30' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-medium uppercase tracking-wide text-fieldiq-muted bg-fieldiq-border/40 px-1.5 py-0.5 rounded">
                        {item.type}
                      </span>
                    </div>
                    <p
                      className={`text-sm truncate ${
                        isDiscount ? 'text-red-400 italic' : 'text-white'
                      }`}
                    >
                      {item.description}
                    </p>
                    <p className="text-fieldiq-muted text-xs mt-0.5">
                      {item.quantity} {item.unit} x {fmtUnitRate(item.rate)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-sm font-semibold ${
                        isDiscount ? 'text-red-400' : 'text-white'
                      }`}
                    >
                      {isDiscount ? '-' : ''}
                      {fmtCurrency(Math.abs(item.total))}
                    </span>
                    <button
                      onClick={() => startEditLine(item)}
                      className="text-fieldiq-muted hover:text-fieldiq-accent transition-colors p-1"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteLineItem(item.id)}
                      className="text-fieldiq-muted hover:text-red-400 transition-colors p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Action Buttons ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => {
              setShowAddLine(!showAddLine);
              setShowAddDiscount(false);
              setShowAddTicket(false);
              setShowRemoveTicket(false);
            }}
            className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-fieldiq-accent/50 transition-colors"
          >
            <Plus size={16} className="text-fieldiq-accent" />
            Add Custom Line
          </button>
          <button
            onClick={() => {
              setShowAddDiscount(!showAddDiscount);
              setShowAddLine(false);
              setShowAddTicket(false);
              setShowRemoveTicket(false);
            }}
            className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-fieldiq-accent/50 transition-colors"
          >
            <Percent size={16} className="text-fieldiq-accent" />
            Add Discount
          </button>
          <button
            onClick={() => {
              setShowAddTicket(!showAddTicket);
              setShowAddLine(false);
              setShowAddDiscount(false);
              setShowRemoveTicket(false);
            }}
            className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-fieldiq-accent/50 transition-colors"
          >
            <Plus size={16} className="text-fieldiq-accent" />
            Add Ticket
          </button>
          <button
            onClick={() => {
              setShowRemoveTicket(!showRemoveTicket);
              setShowAddLine(false);
              setShowAddDiscount(false);
              setShowAddTicket(false);
            }}
            disabled={!invoice.tickets || invoice.tickets.length === 0}
            className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-red-500/50 transition-colors disabled:opacity-40"
          >
            <Trash2 size={16} className="text-red-400" />
            Remove Ticket
          </button>
        </div>

        {/* ── Add Custom Line Form ───────────────────────────────────── */}
        {showAddLine && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-accent/30 space-y-3">
            <h3 className="text-white font-semibold text-sm">Add Custom Line Item</h3>

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Type</label>
              <select
                value={addLineForm.type}
                onChange={(e) => setAddLineForm((p) => ({ ...p, type: e.target.value }))}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="service">Service</option>
                <option value="equipment">Equipment</option>
                <option value="material">Material</option>
                <option value="labor">Labor</option>
                <option value="mileage">Mileage</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Description</label>
              <input
                type="text"
                value={addLineForm.description}
                onChange={(e) => setAddLineForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Line item description"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Unit</label>
                <input
                  type="text"
                  value={addLineForm.unit}
                  onChange={(e) => setAddLineForm((p) => ({ ...p, unit: e.target.value }))}
                  placeholder="each"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Rate</label>
                <input
                  type="number"
                  value={addLineForm.rate}
                  onChange={(e) => setAddLineForm((p) => ({ ...p, rate: e.target.value }))}
                  placeholder="0.00"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Qty</label>
                <input
                  type="number"
                  value={addLineForm.quantity}
                  onChange={(e) => setAddLineForm((p) => ({ ...p, quantity: e.target.value }))}
                  placeholder="1"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddLine(false)}
                className="text-fieldiq-muted text-sm px-3 py-2 rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLineItem}
                disabled={addLineItemMutation.isPending || !addLineForm.description || !addLineForm.rate}
                className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2 text-sm flex items-center gap-1 disabled:opacity-40"
              >
                <Plus size={14} />
                {addLineItemMutation.isPending ? 'Adding...' : 'Add Line'}
              </button>
            </div>
          </div>
        )}

        {/* ── Add Discount Form ──────────────────────────────────────── */}
        {showAddDiscount && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-accent/30 space-y-3">
            <h3 className="text-white font-semibold text-sm">Add Discount</h3>

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Discount Type</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setDiscountForm((p) => ({ ...p, discountType: 'flat' }))}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                    discountForm.discountType === 'flat'
                      ? 'bg-fieldiq-accent text-black border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-white border-fieldiq-border'
                  }`}
                >
                  <DollarSign size={14} className="inline mr-1" />
                  Flat Amount
                </button>
                <button
                  onClick={() => setDiscountForm((p) => ({ ...p, discountType: 'percent' }))}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                    discountForm.discountType === 'percent'
                      ? 'bg-fieldiq-accent text-black border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-white border-fieldiq-border'
                  }`}
                >
                  <Percent size={14} className="inline mr-1" />
                  Percentage
                </button>
              </div>
            </div>

            {discountForm.discountType === 'flat' ? (
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Amount ($)</label>
                <input
                  type="number"
                  value={discountForm.amount}
                  onChange={(e) => setDiscountForm((p) => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            ) : (
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Percent (%)</label>
                <input
                  type="number"
                  value={discountForm.percent}
                  onChange={(e) => setDiscountForm((p) => ({ ...p, percent: e.target.value }))}
                  placeholder="0"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            )}

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Description</label>
              <input
                type="text"
                value={discountForm.description}
                onChange={(e) => setDiscountForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Discount description"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddDiscount(false)}
                className="text-fieldiq-muted text-sm px-3 py-2 rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDiscount}
                disabled={addDiscountMutation.isPending}
                className="bg-red-600 text-white rounded-xl px-4 py-2 text-sm font-bold flex items-center gap-1 disabled:opacity-40"
              >
                <Percent size={14} />
                {addDiscountMutation.isPending ? 'Adding...' : 'Add Discount'}
              </button>
            </div>
          </div>
        )}

        {/* ── Add Ticket Panel ───────────────────────────────────────── */}
        {showAddTicket && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-accent/30 space-y-3">
            <h3 className="text-white font-semibold text-sm">Add Ticket to Invoice</h3>

            <input
              type="text"
              value={ticketSearch}
              onChange={(e) => setTicketSearch(e.target.value)}
              placeholder="Search tickets..."
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
            />

            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredTickets.length === 0 && (
                <p className="text-fieldiq-muted text-sm text-center py-4">
                  No available tickets found.
                </p>
              )}
              {filteredTickets.map((t: any) => (
                <button
                  key={t.id}
                  onClick={() => addTicketMutation.mutate(t.id)}
                  disabled={addTicketMutation.isPending}
                  className="w-full text-left bg-fieldiq-dark rounded-lg px-3 py-2 flex items-center justify-between hover:border-fieldiq-accent/50 border border-fieldiq-border transition-colors disabled:opacity-40"
                >
                  <div>
                    <p className="text-white text-sm font-medium">
                      {t.ticketNumber || t.id.slice(0, 8)}
                    </p>
                    <p className="text-fieldiq-muted text-xs">
                      {t.customer || 'No customer'} &middot; {t.date || ''}
                    </p>
                  </div>
                  <span className="text-fieldiq-accent text-sm font-semibold">
                    {fmtCurrency(t.total)}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowAddTicket(false)}
                className="text-fieldiq-muted text-sm px-3 py-2 rounded-lg hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── Remove Ticket Panel ────────────────────────────────────── */}
        {showRemoveTicket && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-red-500/30 space-y-3">
            <h3 className="text-white font-semibold text-sm">Remove Ticket from Invoice</h3>

            {(invoice.tickets || []).length === 0 && (
              <p className="text-fieldiq-muted text-sm text-center py-4">
                No tickets linked to this invoice.
              </p>
            )}

            <div className="space-y-1">
              {(invoice.tickets || []).map((t) => (
                <div
                  key={t.id}
                  className="bg-fieldiq-dark rounded-lg px-3 py-2 flex items-center justify-between border border-fieldiq-border"
                >
                  <div>
                    <p className="text-white text-sm font-medium">{t.ticketNumber}</p>
                    <p className="text-fieldiq-muted text-xs">
                      {t.date || ''} &middot; {fmtCurrency(t.total)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveTicket(t.id)}
                    disabled={removeTicketMutation.isPending}
                    className="bg-red-600 text-white rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1 disabled:opacity-40"
                  >
                    <Trash2 size={12} />
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowRemoveTicket(false)}
                className="text-fieldiq-muted text-sm px-3 py-2 rounded-lg hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── Totals Summary ─────────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
          <h2 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
            <DollarSign size={16} className="text-fieldiq-accent" />
            Totals
          </h2>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-fieldiq-muted">Subtotal</span>
              <span className="text-white">{fmtCurrency(invoice.subtotal)}</span>
            </div>
            {(invoice.discountTotal || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-red-400">Discounts</span>
                <span className="text-red-400">-{fmtCurrency(invoice.discountTotal)}</span>
              </div>
            )}
            {(invoice.tax1Amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-fieldiq-muted">Tax 1</span>
                <span className="text-white">{fmtCurrency(invoice.tax1Amount)}</span>
              </div>
            )}
            {(invoice.tax2Amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-fieldiq-muted">Tax 2</span>
                <span className="text-white">{fmtCurrency(invoice.tax2Amount)}</span>
              </div>
            )}
            <div className="border-t border-fieldiq-border pt-2 flex justify-between text-base font-bold">
              <span className="text-white">Total</span>
              <span className="text-white">{fmtCurrency(invoice.total)}</span>
            </div>
            <div className="flex justify-between text-base font-bold">
              <span className="text-fieldiq-accent">Balance Due</span>
              <span className="text-fieldiq-accent">{fmtCurrency(invoice.balanceDue)}</span>
            </div>
          </div>
        </div>

        {/* ── Preview PDF Button ─────────────────────────────────────── */}
        <button
          onClick={handlePreviewPdf}
          className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-fieldiq-accent/50 transition-colors"
        >
          <DollarSign size={16} className="text-fieldiq-accent" />
          Preview PDF
        </button>
      </div>

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      {toast && (
        <div
          className={`fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl text-sm font-medium shadow-lg ${
            toast.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
